import { islandModule, type GameId } from '@ezishizi/learning-core';
import type { useNavigate } from '@tanstack/react-router';
import { STOP_MODULE, type PathNode } from './path.ts';
import { store } from './store/index.ts';
import { useApp } from '../state/app.ts';
import { useSession } from '../state/session.ts';

type Navigate = ReturnType<typeof useNavigate>;

/**
 * Moves a profile on to the next lesson (`lib/path.ts`'s `lessonToAdvanceTo` decides which one)
 * and refreshes the cached profile everywhere it's held: the signed-in list and the active
 * profile. Shared by Path.tsx's own "finished every stop" effect and the result screen's "Next"
 * button, so a child who taps Next straight off a lesson's last stop still has the move saved,
 * not just acted on for this one navigation.
 */
export function persistLessonAdvance(profileId: string, lessonId: string): Promise<void> {
  return store.updateProfile(profileId, { currentLessonId: lessonId }).then((updated) => {
    const cached = useApp.getState().profiles;
    useApp.getState().setProfiles((cached ?? []).map((p) => (p.id === updated.id ? updated : p)));
    // The child may have switched sibling while the request was in flight: only the list is
    // refreshed then, or the active profile would flip back to the one they just left.
    if (useApp.getState().profile?.id === updated.id) useApp.getState().setProfile(updated);
  });
}

/**
 * The two formats a stop offers to choose between, or null when it is not a choice stop or the
 * child is partway through one of them: a stop left mid-way resumes the format they chose, so
 * tapping it again must not ask a second time (and switching would throw the run away).
 */
export function pendingChoice(node: PathNode): readonly [GameId, GameId] | null {
  if (node.kind !== 'game' || !node.game || !node.alt) return null;
  return resumedGame(node) ? null : [node.game, node.alt];
}

/** The format of the unfinished run persisted for this choice stop, if there is one. */
function resumedGame(node: PathNode): GameId | null {
  if (node.kind !== 'game' || !node.game || !node.alt) return null;
  const s = useSession.getState();
  if (
    s.profileId !== useApp.getState().profile?.id ||
    s.lessonId !== node.lessonId ||
    s.setId !== node.setId ||
    s.module !== islandModule(node.slot) ||
    s.questions.length === 0 ||
    s.index >= s.questions.length ||
    (s.phase !== 'question' && s.phase !== 'feedback')
  )
    return null;
  return s.game === node.game || s.game === node.alt ? (s.game as GameId) : null;
}

/**
 * Opens whatever a path stop leads to: a word page, the runner for a land/game stop, the reward
 * story or game, or the practice runner. Shared by the path map (tapping a stop) and the result
 * screen's "Next" button, so the two never drift apart on how a stop is opened.
 */
export function goToNode(nav: Navigate, node: PathNode, chosen?: GameId): void {
  if (node.kind === 'word') {
    void nav({
      to: '/word/$lessonId/$setId/$char',
      params: { lessonId: node.lessonId, setId: node.setId, char: node.char },
    });
    return;
  }
  if (node.kind === 'reward') {
    if (node.reward.type === 'story') {
      void nav({
        to: '/story/$lessonId/$storyId',
        params: { lessonId: node.lessonId, storyId: node.reward.storyId },
        search: { mode: 'watch', from: 'path' },
      });
    } else {
      // The pier disc is labelled with the format the picker set aside for it, so it has to
      // travel: without it the runner falls through to a plain mixed round and the disc lies.
      void nav({
        to: '/play/$lessonId/$module',
        params: { lessonId: node.lessonId, module: 'game' },
        search: {
          set: node.setId,
          from: 'path',
          ...(node.reward.game ? { game: node.reward.game } : {}),
        },
      });
    }
    return;
  }
  if (node.kind === 'practice') {
    void nav({
      to: '/practice/$lessonId/$practiceId',
      params: { lessonId: node.lessonId, practiceId: node.practiceId },
      search: { from: 'path' },
    });
    return;
  }
  if (node.kind === 'game') {
    // The module is the slot, so progress survives the format changing; the format itself
    // travels in `game`, and a slot with nothing playable runs the plain drill it replaced. A
    // choice stop plays what the child picked, else the run they left half-played.
    const game = chosen ?? resumedGame(node) ?? node.game;
    void nav({
      to: '/play/$lessonId/$module',
      params: { lessonId: node.lessonId, module: islandModule(node.slot) },
      search: {
        set: node.setId,
        from: 'path',
        ...(game ? { game } : {}),
      },
    });
    return;
  }
  void nav({
    to: '/play/$lessonId/$module',
    params: { lessonId: node.lessonId, module: STOP_MODULE[node.kind] },
    search: { set: node.setId, from: 'path' },
  });
}
